import { useEffect, useState, type FormEvent } from 'react';
import { adminSignupGateway } from '../../admin-signup/services/AdminSignupGateway';
import type { InvitationValidity } from '../../admin-signup/types';

/**
 * The second of Admin Sign Up's two narrowly-scoped public routes (Decision 4). Reached via an invitation
 * link containing the raw token as a query parameter — validates it before ever showing a form, and
 * handles the invalid/expired/already-used/not-finalized-yet states explicitly rather than showing a
 * generic error.
 */
export function AcceptInvitationPage() {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  const [validity, setValidity] = useState<InvitationValidity | 'Checking'>(token ? 'Checking' : 'NotFound');
  // Only ever meaningful once validity is 'Valid' — see InvitationValidityResponse's own doc comment on
  // why an invalid/expired/unknown token can never expose this.
  const [existingAccount, setExistingAccount] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      return;
    }
    adminSignupGateway
      .checkInvitation(token)
      .then((r) => {
        setValidity(r.validity);
        setExistingAccount(r.inviteeHasExistingAccount);
      })
      .catch(() => setValidity('NotFound'));
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // EPN supports multi-company users — one global Escochex identity and password (see
      // RegistrationService.AcceptInvitationAsync's own doc comment). An invitee who already has an
      // account never submits a name or password here at all — this acceptance is membership-only, and
      // the backend never reads/validates/replaces their existing profile/credential regardless of what
      // this sends, but omitting both entirely keeps that contract visible on this side too, not just
      // enforced silently on the server.
      await adminSignupGateway.acceptInvitation(token, existingAccount ? null : displayName, existingAccount ? null : password);
      setAccepted(true);
    } catch {
      setError(
        'This invitation could not be accepted. It may be invalid, expired, already used, or the company may still be finishing its setup — try again shortly.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (validity === 'Checking') {
    return (
      <div className="page page--centered">
        <p className="muted">Checking your invitation…</p>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="page page--centered">
        <section className="card">
          <h1>{existingAccount ? "You're all set" : 'Account created'}</h1>
          <p className="muted">
            {existingAccount
              ? 'This company has been added to your account. Head over to the sign-in page and log in with your existing Escochex credentials.'
              : 'Head over to the sign-in page and log in with your new credentials.'}
          </p>
          <a href="/">
            <button type="button">Sign In</button>
          </a>
        </section>
      </div>
    );
  }

  if (validity !== 'Valid') {
    const messages: Record<Exclude<InvitationValidity, 'Valid'>, string> = {
      Expired: 'This invitation link has expired.',
      AlreadyUsed: 'This invitation has already been used.',
      NotFound: 'This invitation link is invalid.',
    };
    return (
      <div className="page page--centered">
        <section className="card">
          <p className="error" role="alert">
            {messages[validity]}
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="page page--centered">
      <form className="card" onSubmit={handleSubmit}>
        <h1>{existingAccount ? 'Join this company' : 'Set up your account'}</h1>
        {existingAccount && (
          <p className="muted" data-testid="existing-account-notice">
            You already have an Escochex account. Accepting will add this company to your account — sign in
            with your existing password, no new credentials needed.
          </p>
        )}
        {!existingAccount && (
          <>
            <label htmlFor="displayName">Name</label>
            <input id="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required autoFocus />

            <label htmlFor="password">Password</label>
            <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </>
        )}

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : existingAccount ? 'Join Company' : 'Create Account'}
        </button>
      </form>
    </div>
  );
}
